import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from 'react';
import { i18n } from 'src/common';
import { InputLabel } from 'src/components';
import { colors as themeColors } from 'src/theme';

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const UploadButton = styled.label((props: { disabled?: boolean }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '10px 16px',
  borderRadius: '8px',
  border: `1px dashed ${themeColors.primary1}`,
  color: themeColors.primary1,
  backgroundColor: themeColors.background6,
  fontSize: '16px',
  lineHeight: '18px',
  fontWeight: 700,
  cursor: props.disabled ? 'not-allowed' : 'pointer',
  opacity: props.disabled ? 0.6 : 1,
  textAlign: 'center' as const,

  'input': {
    position: 'absolute' as const,
    width: 0,
    height: 0,
    opacity: 0,
  },
}));

const SelectedFile = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '10px 16px',
  borderRadius: '8px',
  backgroundColor: themeColors.background6,
  fontSize: '14px',
  color: themeColors.text2,
});

const RemoveButton = styled.button({
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: themeColors.error1,
  fontSize: '18px',
  lineHeight: 1,
  padding: 0,
});

const ValidationError = styled.div({
  color: themeColors.error1,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  paddingTop: '4px',
});

interface InputFileUploadProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  id?: string;
  accept?: string;
  maxSizeMb?: number;
}

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const InputFileUpload: React.FC<InputFileUploadProps> = ({
  value, onChange, label, error, disabled, id, accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx', maxSizeMb = 15
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [localError, setLocalError] = useState<string | undefined>(undefined);

  // Keep the displayed filename in sync with the form value (e.g. reset when cleared externally)
  useEffect(() => {
    if (!value) {
      setFileName(undefined);
      return;
    }
    try {
      const parsed = JSON.parse(value);
      setFileName(parsed?.fileName);
    } catch (_) {
      setFileName(undefined);
    }
  }, [value]);

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalError(undefined);
    const file = event.target.files?.[0];
    // Allow re-selecting the same file after a clear
    event.target.value = '';
    if (!file) return;

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setLocalError(i18n.t('components.forms.policyCancellationForm.fileType'));
      onChange?.(undefined);
      setFileName(undefined);
      return;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      setLocalError(i18n.t('components.forms.policyCancellationForm.fileTooLarge'));
      onChange?.(undefined);
      setFileName(undefined);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      setFileName(file.name);
      onChange?.(JSON.stringify({ base64, fileName: file.name, mimeType: file.type }));
    };
    reader.onerror = () => {
      setLocalError(i18n.t('components.forms.policyCancellationForm.fileType'));
      onChange?.(undefined);
      setFileName(undefined);
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    setFileName(undefined);
    setLocalError(undefined);
    onChange?.(undefined);
  };

  const displayedError = error || localError;

  return (
    <Wrapper>
      {label ? <InputLabel>{label}</InputLabel> : null}
      {fileName ? (
        <SelectedFile>
          <span>{fileName}</span>
          {!disabled && (
            <RemoveButton type="button" onClick={handleRemove} aria-label="Ukloni datoteku">×</RemoveButton>
          )}
        </SelectedFile>
      ) : (
        <UploadButton htmlFor={id} disabled={disabled}>
          {i18n.t('components.forms.policyCancellationForm.uploadButton')}
          <input
            id={id}
            ref={inputRef}
            type="file"
            accept={accept}
            disabled={disabled}
            onChange={handleSelect}
          />
        </UploadButton>
      )}
      {displayedError ? <ValidationError>{displayedError}</ValidationError> : null}
    </Wrapper>
  );
};
